import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import CellContent from './CellContent'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { partitionBy } from 'lib/array/arrayUtils'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'

interface Props {
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters: App.OfferListFilters
  selectionId: string
  selections: Array<App.OfferComparisonSelection>
}

function isInclusionInAllOtherOffers(
  inclusion: App.OfferInclusion,
  currentSelectionId: string,
  selections: Array<App.OfferComparisonSelection>,
  bedbankOfferRates: any,
): boolean {
  const otherSelections = selections.filter(s => s.selectionId !== currentSelectionId)

  return otherSelections.every(selection => {
    const otherFilters = selection.params?.filters
    if (!otherFilters) return false

    const otherRateKey = getBedbankRateKey(otherFilters.rooms || [], otherFilters.checkIn || '', otherFilters.checkOut || '')
    const otherRate = bedbankOfferRates[selection.offerId]?.[otherRateKey]?.find((r: any) => r.id === otherFilters.roomRateId)
    const otherInclusions = otherRate?.inclusions || []

    return otherInclusions.some((otherInclusion: App.OfferInclusion) => otherInclusion.description === inclusion.description)
  })
}

function BedbankHotelExclusiveInclusionsCell({
  offer,
  filters,
  selectionId,
  selections,
}: Props) {
  const intl = useIntl()
  const promotions = useBedbankRoomRatePromotions(offer?.promotions, filters.checkIn, filters.checkOut)
  const bedbankOfferRates = useAppSelector(state => state.offer.bedbankOfferRates)
  const rateKey = getBedbankRateKey(filters.rooms || [], filters.checkIn || '', filters.checkOut || '')
  const rate = useMemo<App.BedbankRate | undefined>(() => {
    return bedbankOfferRates[offer.id]?.[rateKey]?.find(r => r.id === filters.roomRateId)
  }, [bedbankOfferRates, offer.id, filters.roomRateId, rateKey])

  const promotionalInclusions = useMemo(() => {
    if (!rate?.promotionId || !promotions[rate.promotionId]) return
    return promotions[rate.promotionId].inclusions
  }, [rate, promotions])

  const [commonInclusions, exclusiveInclusions] = useMemo(() => {
    const inclusions = promotionalInclusions || rate?.inclusions || []
    return partitionBy(inclusions, inclusion => isInclusionInAllOtherOffers(inclusion, selectionId, selections, bedbankOfferRates))
  }, [rate, selectionId, selections, bedbankOfferRates, promotionalInclusions])

  const hasAnyInclusions = exclusiveInclusions.length || commonInclusions.length

  return <CellContent>
    {hasAnyInclusions && <VerticalSpacer gap={8}>
      <BodyText variant="medium" weight="bold">{intl.formatMessage({
        id: 'vAMsZNPr0Gm_ednYNRYV',
        defaultMessage: 'Inclusions:',
        description: 'Section heading for exclusive inclusions in bedbank hotel offer comparison',
      })}
      </BodyText>
      <Group direction="vertical" gap={4}>
        {exclusiveInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          isHighlighted
        />)}
        {commonInclusions.map((inclusion) => <OfferInclusionItem
          key={inclusion.id}
          inclusion={inclusion}
          isHighlighted={false}
        />)}
      </Group>
    </VerticalSpacer>}
  </CellContent>
}

export default BedbankHotelExclusiveInclusionsCell
